/**
 * Resolución del total de una factura agrupada a partir de varias filas de
 * Excel (una por tipo de IVA). Los ERP siguen dos convenciones distintas:
 *
 *  - Total GLOBAL repetido en cada fila (p. ej. 15.255,40 en todas) → se conserva.
 *  - Total POR LÍNEA (base + IVA de esa línea; p. ej. Meribia) → se acumulan.
 *
 * La detección compara ambas hipótesis con la suma de bases + IVA (+ RE) − IRPF
 * de las líneas agrupadas y elige la que cuadra. Si ninguna cuadra, se conserva
 * el primer total (comportamiento anterior).
 */

export interface VatLineLike {
  taxableBase: number;
  vatAmount: number;
  vatRate?: number;
  /** Recargo de equivalencia (emitidas). */
  reAmount?: number;
  /** Recargo de equivalencia (recibidas). */
  surchargeAmount?: number;
}

export interface InvoiceLike {
  totalAmount: number;
  irpfAmount?: number | null;
  vatLines: VatLineLike[];
}

const EPS = 0.05;
const approx = (a: number, b: number): boolean => Math.abs(a - b) <= EPS;
const round2 = (n: number): number => Math.round(n * 100) / 100;

export const resolveGroupedTotal = <T extends InvoiceLike>(
  invoice: T,
  lineTotals: number[],
): T => {
  if (lineTotals.length <= 1) return invoice;

  const expected = round2(
    invoice.vatLines.reduce(
      (acc, l) => acc + l.taxableBase + l.vatAmount + (l.reAmount ?? l.surchargeAmount ?? 0),
      0,
    ) - (invoice.irpfAmount ?? 0),
  );
  const sumLines = round2(lineTotals.reduce((a, b) => a + b, 0));
  const allEqual = lineTotals.every((t) => approx(t, lineTotals[0]));

  if (allEqual) {
    // Total repetido en cada fila. Normalmente es el global; pero si el valor
    // repetido no cuadra con el esperado y la suma sí, eran totales de línea
    // idénticos (dos líneas con el mismo importe) y hay que acumular.
    if (!approx(lineTotals[0], expected) && approx(sumLines, expected)) {
      return { ...invoice, totalAmount: sumLines };
    }
    return invoice;
  }

  // Totales distintos por fila: si la suma cuadra con bases + IVA − IRPF,
  // el ERP exporta totales por línea → acumular.
  if (approx(sumLines, expected)) {
    return { ...invoice, totalAmount: sumLines };
  }

  return invoice;
};
